import { isEmpty } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LookupsAPI from "../../API/lookups.api";
import ScansSchema from "../../schemas/scans.schema";
import { BOOLEAN_DROPDOWN, FILTER_TYPES, ROLES } from "../../utils/constants";
import { shortDateYMD } from "../../utils/DateHelper";
import { mapLookupToSelect } from "../../utils/Helpers";
import { validate } from "../../utils/ValidationHelpers";
import HorizontalForm from "../Forms/HorizontalForm";

const ScanFiltersForm = ({
    input,
    setInput,
    materialsLookup,
    disableEditFilter,
    applyFilter,
    mappingToForm,
    materialsLoading,
    thresholdStartState,
    getIsOutlierValue,
}) => {
    const { t } = useTranslation();
    // Lookups
    const [usersLookup, setUsersLookup] = useState([]);
    const [scannersLookup, setScannersLookup] = useState([]);
    const [modelsLookup, setModelsLookup] = useState([]);
    const [filteredModelsLookup, setFilteredModelsLookup] = useState([]);
    const [lotsLookup, setLotsLookup] = useState([]);
    const [filteredLotsLookup, setFilteredLotsLookup] = useState([]);
    const [parameterLookup, setParameterLookup] = useState([]);
    const [suppliersLookup, setSuppliersLookup] = useState([]);
    // Form
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [error, setError] = useState({});

    const isCompatibleRef = useRef(false);
    //Dropdowns loading states
    const [loadingStates, setLoadingStates] = useState({
        usersLookup: false,
        scannersLookup: false,
        modelsLookup: false,
        lotsLookup: false,
        parametersLookup: false,
        suppliersLookup: false,
    });

    useEffect(() => {
        getUsersLookup();
        getScannersLookup();
        getModelsLookup();
        getLotsLookup();
    }, []);

    useEffect(() => {
        if (input.material) {
            getParameterNamesForThreshold();
            getSuppliersLookup();
        }
        if (!mappingToForm) handleMaterialChangeEffect();
        if (!isEmpty(input.material)) {
            setFilteredModelsLookup(
                modelsLookup.filter(
                    (model) => model.materialId === input.material?.value
                )
            );
            setFilteredLotsLookup(
                lotsLookup.filter(
                    (lot) => lot.materialId === input.material?.value
                )
            );
        } else {
            setFilteredModelsLookup(modelsLookup);
            setFilteredLotsLookup(lotsLookup);
        }
    }, [input.material]);

    useEffect(() => {
        if (!mappingToForm) handleLotsChangeEffect();
    }, [input.lots]);

    useEffect(() => {
        if (!mappingToForm) handleModelsChangeEffect();
    }, [input.models]);

    useEffect(() => {
        if (!mappingToForm) handleStartDateChangeEffect();
    }, [input.capturedStartDate]);

    const getUsersLookup = async () => {
        _setLoadingStates("usersLookup", true);
        const result = await LookupsAPI.getUsers();
        setUsersLookup(mapLookupToSelect(result, "email"));
        _setLoadingStates("usersLookup", false);
    };

    const getScannersLookup = async () => {
        _setLoadingStates("scannersLookup", true);
        const result = await LookupsAPI.getScanners(false);
        setScannersLookup(result);
        _setLoadingStates("scannersLookup", false);
    };

    const getModelsLookup = async () => {
        _setLoadingStates("modelsLookup", true);
        const result = await LookupsAPI.getUsedModelsInScans();
        setModelsLookup(result);
        setFilteredModelsLookup(result);
        _setLoadingStates("modelsLookup", false);
    };

    const getLotsLookup = async () => {
        _setLoadingStates("lotsLookup", true);
        const result = await LookupsAPI.getUsedLotsInScans();
        setLotsLookup(result);
        setFilteredLotsLookup(result);
        _setLoadingStates("lotsLookup", false);
    };

    const getSuppliersLookup = async () => {
        _setLoadingStates("suppliersLookup", true);
        const result = await LookupsAPI.getUsedSuppliersInScans(
            input.material.value
        );
        setSuppliersLookup(result);
        _setLoadingStates("suppliersLookup", false);
    };

    const getParameterNamesForThreshold = async () => {
        _setLoadingStates("parametersLookup", true);
        const result = await LookupsAPI.getUsedParametersForScans(
            input.material.value
        );
        setParameterLookup(result);
        _setLoadingStates("parametersLookup", false);
    };

    // setting loading state using key
    const _setLoadingStates = (key, value) => {
        setLoadingStates((prev) => ({ ...prev, [key]: value }));
    };

    const handleStartDateChangeEffect = () => {
        setInput((prevInput) => ({
            ...prevInput,
            capturedEndDate:
                new Date(input.capturedStartDate) >
                new Date(input.capturedEndDate)
                    ? ""
                    : prevInput.capturedEndDate,
        }));
    };

    const handleMaterialChangeEffect = () => {
        if (!isCompatibleRef.current) {
            setInput((prevInput) => ({
                ...prevInput,
                threshold: thresholdStartState,
                lots: [],
                models: [],
            }));
        }
        isCompatibleRef.current = false;
    };

    const handleLotsChangeEffect = () => {
        const firstLotMaterialId = input?.lots?.[0]?.materialId;
        if (!input?.material?.value && firstLotMaterialId) {
            const lotMaterial = materialsLookup.find(
                (material) => material.value === firstLotMaterialId
            );

            isCompatibleRef.current = true;

            setInput((prevInput) => ({
                ...prevInput,
                material: lotMaterial,
            }));
        }
    };

    const handleModelsChangeEffect = () => {
        const firstModelMaterialId = input?.models?.[0]?.materialId;
        if (!input?.material?.value && firstModelMaterialId) {
            const modelMaterial = materialsLookup.find(
                (material) => material.value === firstModelMaterialId
            );

            isCompatibleRef.current = true;

            setInput((prevInput) => ({
                ...prevInput,
                material: modelMaterial,
            }));
        }
    };

    const getParameterLabelStyle = (parameter) => {
        return (
            <>
                {parameter.label}
                {parameter.unit && (
                    <span className="ml-1 text-xxs text-primary-color-orange">
                        ({parameter.unit})
                    </span>
                )}
            </>
        );
    };

    const fields = [
        {
            required: true,
            label: t("material"),
            name: "material",
            type: FILTER_TYPES.select,
            placeholder: t("selectPlaceholder", { entity: t("material") }),
            isClearable: true,
            options: materialsLookup,
            isLoading: materialsLoading,
        },
        {
            label: t("models"),
            name: "models",
            type: FILTER_TYPES.select,
            placeholder: t("selectPlaceholder", { entity: t("models") }),
            isMulti: true,
            options: filteredModelsLookup,
            emptyMessage: t("noModelsForThisMaterial"),
            isLoading: loadingStates.modelsLookup,
        },
        {
            label: t("lots"),
            name: "lots",
            type: FILTER_TYPES.select,
            placeholder: t("selectPlaceholder", { entity: t("lots") }),
            isMulti: true,
            options: filteredLotsLookup,
            emptyMessage: t("noLotsForThisMaterial"),
            isLoading: loadingStates.lotsLookup,
        },
        {
            label: t("suppliers"),
            name: "suppliers",
            type: FILTER_TYPES.select,
            placeholder: t("selectPlaceholder", { entity: t("suppliers") }),
            isMulti: true,
            options: suppliersLookup,
            isLoading: loadingStates.suppliersLookup,
        },
        {
            label: `${t("capture")} - ${t("startDate")}`,
            name: "capturedStartDate",
            type: FILTER_TYPES.date,
            max: shortDateYMD(new Date()),
        },
        {
            label: `${t("capture")} - ${t("endDate")}`,
            name: "capturedEndDate",
            type: FILTER_TYPES.date,
            max: shortDateYMD(new Date()),
            ...(input.capturedStartDate && { min: input.capturedStartDate }),
        },
        {
            label: t("createdBy"),
            name: "createdByUsers",
            type: FILTER_TYPES.select,
            placeholder: t("selectPlaceholder", { entity: t("createdBy") }),
            isMulti: true,
            options: usersLookup,
            isLoading: loadingStates.usersLookup,
            roles: [
                ROLES.admin,
                ROLES.partnerAdmin,
                ROLES.superAdmin,
                ROLES.support,
            ],
        },
        {
            label: t("onBehalfOf"),
            name: "onBehalfOf",
            type: FILTER_TYPES.select,
            placeholder: t("selectPlaceholder", { entity: t("onBehalfOf") }),
            isMulti: true,
            options: usersLookup,
            isLoading: loadingStates.usersLookup,
            roles: [
                ROLES.admin,
                ROLES.partnerAdmin,
                ROLES.superAdmin,
                ROLES.support,
            ],
        },
        {
            label: t("devices"),
            name: "devices",
            type: FILTER_TYPES.select,
            placeholder: t("selectPlaceholder", { entity: t("devices") }),
            isMulti: true,
            options: scannersLookup,
            isLoading: loadingStates.scannersLookup,
        },
        {
            label: t("sampleName"),
            name: "sampleName",
            type: FILTER_TYPES.text,
            placeholder: t("sampleNamePlaceholder"),
        },
        {
            label: t("note"),
            name: "note",
            type: FILTER_TYPES.text,
            placeholder: t("notesPlaceholder"),
        },
        {
            label: t("markedAsOutlier"),
            name: "isOutlier",
            type: FILTER_TYPES.radio,
            options: Object.values(BOOLEAN_DROPDOWN),
            handler: (e) => {
                setInput((prevInput) => ({
                    ...prevInput,
                    isOutlier: getIsOutlierValue(e.target.value),
                }));
            },
        },
        {
            label: t("threshold"),
            targetPlaceholder: t("parameterName"),
            placeholder: t("threshold"),
            name: "threshold",
            type: FILTER_TYPES.compare,
            options: parameterLookup.map((param) => ({
                ...param,
                label: getParameterLabelStyle(param),
            })),
            isLoading: loadingStates.parametersLookup,
        },
    ];

    useEffect(() => {
        validate(
            input,
            ScansSchema.ScansPrerequisitesSchema,
            setIsFormValid,
            setError
        );
    }, [input]);

    const handleApply = () => {
        setIsSubmitting(true);

        setIsFormSubmitted(true);
        if (!isFormValid) {
            setIsSubmitting(false);
            return;
        }

        const thresholdSample = input?.threshold;
        thresholdSample.filters = thresholdSample.filters
            ?.filter((filter) => {
                return (
                    filter?.target &&
                    filter?.compare &&
                    filter?.value &&
                    filter?.unit
                );
            })
            .map((filter) => ({ ...filter, value: filter.value }));

        const newFilter = {
            material: input?.material,
            ...(input?.capturedStartDate && {
                capturedStartDate: input?.capturedStartDate,
            }),
            ...(input?.capturedEndDate && {
                capturedEndDate: input?.capturedEndDate,
            }),
            ...(!isEmpty(thresholdSample?.filters) && {
                threshold: thresholdSample,
            }),
            ...(input?.sampleName && { sampleName: input?.sampleName }),
            ...(input?.note && { note: input?.note }),
            lots: !isEmpty(input?.lots) ? input.lots : [],
            models: !isEmpty(input?.models) ? input.models : [],
            createdByUsers: !isEmpty(input?.createdByUsers)
                ? input.createdByUsers
                : [],
            onBehalfOf: !isEmpty(input?.onBehalfOf) ? input.onBehalfOf : [],
            devices: !isEmpty(input?.devices) ? input.devices : [],
            suppliers: !isEmpty(input?.suppliers) ? input.suppliers : [],
            isOutlier: getIsOutlierValue(input?.isOutlier),
        };

        applyFilter(newFilter);
        setIsSubmitting(false);
    };

    return (
        <>
            <HorizontalForm
                fields={fields}
                input={input}
                setInput={(value) => {
                    setInput(value);
                }}
                error={error}
                isFormSubmitted={isFormSubmitted}
                gridCols="2"
            />
            <div className="steps-btn flex items-center justify-end mt-4">
                <button
                    onClick={disableEditFilter}
                    className="default-transparent-btn"
                >
                    {t("cancel")}
                </button>
                <button
                    id="applyButton"
                    className="default-orange-btn"
                    onClick={handleApply}
                    disabled={isSubmitting || !isFormValid}
                >
                    {!isSubmitting && <i className="fas fa-check mr-2"></i>}
                    {isSubmitting ? t("loading") : t("apply")}
                </button>
            </div>
        </>
    );
};

export default ScanFiltersForm;
